import axios from "axios";
import { uintStream, commit } from "../../lib/keno-vendor/engine.mjs";
import { resolve } from "../../lib/keno-vendor/resolve.mjs";

export const KENO_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_SESSION_KEY = "gv_keno_demo_session";
let creatingDemo;
export const gameHeaders = (demo) => demo ? { "X-Keno-Demo": localStorage.getItem(DEMO_SESSION_KEY) } : { Authorization: `Bearer ${localStorage.getItem("gv_token")}` };
const createDemo = () => {
  if (!creatingDemo) creatingDemo = axios.post(`${KENO_API}/keno/demo/session`).then(({ data }) => {
    localStorage.setItem(DEMO_SESSION_KEY, data.session_id); return data.current;
  }).finally(() => { creatingDemo = null; });
  return creatingDemo;
};
export const loadFairness = async (demo) => {
  if (demo && !localStorage.getItem(DEMO_SESSION_KEY)) return createDemo();
  try { return (await axios.get(`${KENO_API}/keno/${demo ? "demo/" : ""}fairness`, { headers: gameHeaders(demo) })).data; }
  catch (error) { if (demo && error.response?.status === 404) return createDemo(); throw error; }
};
export const rotateFairness = async (demo, current, clientSeed) => (await axios.post(`${KENO_API}/keno/${demo ? "demo/" : ""}fairness/rotate`, {
  seed_id: current.seed_id, nonce: current.nonce, client_seed: clientSeed, next_server_seed_hash: current.next_server_seed_hash,
}, { headers: gameHeaders(demo) })).data;

export const calculateKenoRound = async ({ serverSeed, clientSeed, nonce, picks, risk, betMinor, paytableVersion }, config) => {
  const key = `${risk}/${picks.length}`;
  const selectedTable = !paytableVersion || paytableVersion === config.paytable_version ? config.paytables : config.historical_paytables?.[paytableVersion];
  if (!selectedTable?.[key]) throw new Error("Select a valid payout table, difficulty and 1–10 numbers.");
  const hash = await commit(serverSeed);
  const values = await uintStream(serverSeed, clientSeed, nonce, 39);
  const table = { variants: { [key]: { perHit: selectedTable[key].map((value) => Math.round(value * 100000000)) } } };
  return { hash, ...resolve(values, { picks, difficulty: risk }, table, { betMinor }) };
};

export const verifyKenoRound = async (round, demo, config) => {
  let seed = round.server_seed;
  if (!seed) seed = (await axios.get(`${KENO_API}/keno/${demo ? "demo/" : ""}fairness/reveal/${round.seed_id}`, { headers: { ...gameHeaders(demo), 'X-GV-Currency': round.currency || 'v' } })).data.server_seed;
  const hash = await commit(seed);
  if (round.server_seed_hash && hash !== round.server_seed_hash) throw new Error("The revealed seed does not match the committed hash.");
  const values = await uintStream(seed, round.client_seed, round.nonce, 39);
  const key = `${round.risk}/${round.picks.length}`;
  const version = round.paytable_version || "gv-rounded-v1";
  const payouts = version === config.paytable_version ? config.paytables : config.historical_paytables?.[version];
  if (!payouts?.[key]) throw new Error("The payout table for this historical bet is unavailable.");
  const table = { variants: { [key]: { perHit: payouts[key].map((value) => Math.round(value * 100000000)) } } };
  const result = resolve(values, { picks: round.picks, difficulty: round.risk }, table, { betMinor: round.bet_minor });
  const valid = JSON.stringify(result.outcome.drawn) === JSON.stringify(round.drawn) && result.payoutMinor === round.payout_minor && result.outcome.matches === round.matches;
  if (!valid) throw new Error("Verification failed: the draw or payout differs from the recorded result.");
  return { valid, seed, hash };
};